/**
 * Hazard maps and landholder information (Diurnal inventory 04 §4 and §5; phase 2b.5).
 *
 * - Regeneration after a settings change (MB, Q64, decision 91): ask per map, and keep the old version until the new
 *   one succeeds.
 * - Landholder information (MB, Q56, decision 83): boundaries may be shared with clients per project; contacts, notes
 *   and flags are internal only; only managers may export contacts.
 * - Hazards on the client portal only when published for the project (MB, Q55, decision 82).
 */
import type { MapFeature } from './map-layers.js';
import type { LatLng } from './planning.js';

/** Tenant roles (`docs/04-security-and-tenancy.md` §2). A person may hold several. */
export type TenantRole =
  | 'platform_admin'
  | 'tenant_admin'
  | 'manager'
  | 'field_crew'
  | 'office_staff'
  | 'data_processor'
  /** The phone-only sign-in (R156, decision 917): never a staff right on its own. */
  | 'pilot'
  | 'client_admin'
  | 'client_viewer';

const CLIENT_ROLES: ReadonlySet<TenantRole> = new Set<TenantRole>(['client_admin', 'client_viewer']);

/** True when every role is a client role (staff roles win when a person holds both). */
export function isClientOnly(roles: readonly TenantRole[]): boolean {
  return roles.length > 0 && roles.every((r) => CLIENT_ROLES.has(r));
}

/** Hazard generation settings (inventory 04 §4.2). */
export interface HazardSettings {
  /** Search buffer around the boundary, km (1 to 500). */
  readonly buffer_km: number;
  /** Powerline span analyser threshold, m (100 to 5000). */
  readonly span_threshold_m: number;
}

export const DEFAULT_HAZARD_SETTINGS: HazardSettings = { buffer_km: 20, span_threshold_m: 700 };

export function validateHazardSettings(s: HazardSettings): string[] {
  const out: string[] = [];
  if (!Number.isFinite(s.buffer_km) || s.buffer_km < 1 || s.buffer_km > 500) {
    out.push('Hazard buffer must be from 1 km to 500 km');
  }
  if (!Number.isFinite(s.span_threshold_m) || s.span_threshold_m < 100 || s.span_threshold_m > 5000) {
    out.push('Span threshold must be from 100 m to 5000 m');
  }
  return out;
}

/**
 * One generated hazard map (from a boundary file or a drawn area). `settings` are those it was made with, as the
 * file records them (`generated_with`); null for a map whose file does not say (the Diurnal tool's, or one made by
 * Teslyn Field before 2026.10.10).
 */
export interface HazardMap {
  readonly id: string;
  readonly name: string;
  readonly settings: HazardSettings | null;
  readonly version: number;
  readonly generated_at: string;
}

function sameSettings(a: HazardSettings, b: HazardSettings): boolean {
  return a.buffer_km === b.buffer_km && a.span_threshold_m === b.span_threshold_m;
}

/**
 * Maps generated with settings other than `settings`: each is offered for regeneration, one question per map (decision
 * 91). A map whose settings are not recorded is offered too: the Diurnal tool regenerated every map.
 */
export function mapsToOfferRegeneration(maps: readonly HazardMap[], settings: HazardSettings): HazardMap[] {
  return maps.filter((m) => m.settings === null || !sameSettings(m.settings, settings));
}

/** The question asked for one map after a settings change. */
export function regenerationQuestion(map: HazardMap, settings: HazardSettings): string {
  const parts: string[] = [];
  const was = map.settings;
  if (was === null) {
    parts.push(`buffer ${settings.buffer_km} km, span threshold ${settings.span_threshold_m} m`);
  } else {
    if (was.buffer_km !== settings.buffer_km) {
      parts.push(`buffer ${was.buffer_km} km to ${settings.buffer_km} km`);
    }
    if (was.span_threshold_m !== settings.span_threshold_m) {
      parts.push(`span threshold ${was.span_threshold_m} m to ${settings.span_threshold_m} m`);
    }
  }
  const unknown = was === null ? ' The file does not say which settings it was made with.' : '';
  return `Regenerate "${map.name}" with the new settings (${parts.join(', ')})?${unknown} The current map stays until the new one is ready.`;
}

/** What a hazard file records of its making (`generated_with`, written by Teslyn Field from 2026.10.10). */
export interface HazardGeneratedWith {
  readonly buffer_km: number;
  readonly span_threshold_m: number;
  /** When it was made (UTC, ISO). */
  readonly generated_at: string;
}

/** A hazard map file on the map, as `map_hazard_read` returns its set. */
export interface HazardMapFile {
  readonly path: string;
  readonly set: {
    readonly name: string;
    readonly bnd_kml_text: string;
    readonly generated_with?: HazardGeneratedWith | null;
  };
}

/**
 * The maps a settings change can regenerate: those that hold their boundary (a map opened without one cannot be made
 * again). The id is the file's path.
 */
export function regenerableMaps(files: readonly HazardMapFile[]): HazardMap[] {
  return files
    .filter((f) => f.set.bnd_kml_text.trim() !== '')
    .map((f) => {
      const g = f.set.generated_with ?? null;
      return {
        id: f.path,
        name: f.set.name,
        settings: g ? { buffer_km: g.buffer_km, span_threshold_m: g.span_threshold_m } : null,
        version: 1,
        generated_at: g?.generated_at ?? '',
      };
    });
}

/** True when a save changed what a map is made with (the folders a new map shows change no map). */
export function regenerationSettingsChanged(before: HazardSettings, after: HazardSettings): boolean {
  return !sameSettings(before, after);
}

/** A map and its regeneration, if any. The shown map is always `current`. */
export interface HazardMapState {
  readonly current: HazardMap;
  readonly pending: HazardSettings | null;
  readonly status: 'current' | 'regenerating' | 'failed';
  readonly error: string | null;
}

export function hazardMapState(map: HazardMap): HazardMapState {
  return { current: map, pending: null, status: 'current', error: null };
}

/** Start regenerating with new settings; the old map stays shown. */
export function beginRegeneration(state: HazardMapState, settings: HazardSettings): HazardMapState {
  return { ...state, pending: settings, status: 'regenerating', error: null };
}

/**
 * Finish a regeneration: on success the new map replaces the old one (next version); on failure the old map is
 * kept and the error is shown.
 */
export function finishRegeneration(
  state: HazardMapState,
  outcome:
    { readonly ok: true; readonly generated_at: string } | { readonly ok: false; readonly error: string },
): HazardMapState {
  if (state.status !== 'regenerating' || !state.pending) return state;
  if (!outcome.ok) {
    return { ...state, pending: null, status: 'failed', error: outcome.error };
  }
  return {
    current: {
      ...state.current,
      settings: state.pending,
      version: state.current.version + 1,
      generated_at: outcome.generated_at,
    },
    pending: null,
    status: 'current',
    error: null,
  };
}

/** Hazards on the portal map: staff always; clients only when the project publishes hazards to them (Q55). */
export function canViewHazards(roles: readonly TenantRole[], publishedToClient: boolean): boolean {
  return !isClientOnly(roles) || publishedToClient;
}

/** Parts of a landholder (pinned property) record. */
export type LandholderField = 'boundary' | 'name' | 'contacts' | 'notes' | 'flags';

/**
 * Who may see a landholder field (Q56): staff see everything; clients see the boundary and its name only when the
 * project shares boundaries with them; contacts, notes and flags (needs notification, objection) are internal only.
 */
export function canViewLandholderField(
  roles: readonly TenantRole[],
  field: LandholderField,
  boundariesSharedWithClient: boolean,
): boolean {
  if (!isClientOnly(roles)) return true;
  return boundariesSharedWithClient && (field === 'boundary' || field === 'name');
}

/** Only managers may export landholder contacts (Q56). */
export function canExportLandholderContacts(roles: readonly TenantRole[]): boolean {
  return roles.includes('manager');
}

/** A landholder record as exported (KML or CSV); contacts only for a manager. */
export interface LandholderRecord {
  readonly name: string;
  readonly folder: string;
  readonly notes: string;
  readonly needs_notification: boolean;
  readonly objection: boolean;
  readonly contacts: readonly string[];
}

/** Strip what the exporting person may not take out of Teslyn. */
export function landholderForExport(
  record: LandholderRecord,
  roles: readonly TenantRole[],
): LandholderRecord {
  return canExportLandholderContacts(roles) ? record : { ...record, contacts: [] };
}

// ------------------------------------------------------------------------------------------------ generation
// docs/specs/map.md §16: the core (`teslyn_core::planning::hazard_gen`) fetches and builds the map; these are the
// words and states the screen shows around it.

/** Which folders a new hazard map shows (the Diurnal `hazardDefaults`, am:12778): all but Poles. */
export interface HazardFolderDefaults {
  readonly powerlines: boolean;
  readonly poles: boolean;
  readonly lv: boolean;
  readonly swer: boolean;
  readonly hv: boolean;
  readonly tr: boolean;
  readonly span_analyser: boolean;
  readonly towers: boolean;
  readonly matched_towers: boolean;
  readonly inferred_towers: boolean;
  readonly unknown_towers: boolean;
  readonly naips_towers: boolean;
  readonly other_obstacles: boolean;
  readonly wind_turbines: boolean;
  readonly chimneys: boolean;
}

export const DEFAULT_HAZARD_FOLDERS: HazardFolderDefaults = {
  powerlines: true,
  poles: false,
  lv: true,
  swer: true,
  hv: true,
  tr: true,
  span_analyser: true,
  towers: true,
  matched_towers: true,
  inferred_towers: true,
  unknown_towers: true,
  naips_towers: true,
  other_obstacles: true,
  wind_turbines: true,
  chimneys: true,
};

/** The settings form's words for each folder (settings-module.js 1102-1117). */
export const HAZARD_FOLDER_WORDS: ReadonlyArray<{
  readonly key: keyof HazardFolderDefaults;
  readonly word: string;
}> = [
  { key: 'powerlines', word: 'Powerlines (group)' },
  { key: 'poles', word: 'Poles' },
  { key: 'lv', word: 'LV lines' },
  { key: 'swer', word: 'SWER lines' },
  { key: 'hv', word: 'HV lines' },
  { key: 'tr', word: 'TR lines' },
  { key: 'span_analyser', word: 'Span Analyser' },
  { key: 'towers', word: 'Communication Towers (group)' },
  { key: 'matched_towers', word: 'Matched towers' },
  { key: 'inferred_towers', word: 'Inferred towers' },
  { key: 'unknown_towers', word: 'Unknown towers' },
  { key: 'naips_towers', word: 'NOTAM obstacles (Naips Towers)' },
  { key: 'other_obstacles', word: 'Other Obstacles' },
  { key: 'wind_turbines', word: 'Wind turbines (OSM)' },
  { key: 'chimneys', word: 'Chimneys and stacks (OSM)' },
];

const thousands = (n: number): string => Math.round(n).toLocaleString('en-AU');

/** The question before a generation (the tool's confirm dialog, am:12806). */
export function hazardConfirmText(fileName: string, s: HazardSettings): string {
  return (
    `Generate a hazard map from ${fileName}? Teslyn fetches powerlines, communication towers and obstacles within ` +
    `${s.buffer_km} km of the area and marks powerline spans longer than ${s.span_threshold_m} m. Sources: ArcGIS ` +
    `LUAL powerlines and Thomson's tower list (Australia), OpenStreetMap obstacles (worldwide).`
  );
}

/** The large-area question (am:12837). */
export function largeAreaText(areaKm2: number): string {
  return (
    `The search area is about ${thousands(areaKm2)} km². Fetching every OpenStreetMap obstacle, power poles ` +
    `included, over a large area can take a long time. Power poles are the densest layer; leaving them out keeps ` +
    `everything else.`
  );
}

/** A drawn area's name (`showDrawAreaNameDialog`, am:8800: required, up to 80 characters). */
export function drawnAreaNameProblem(name: string): string | null {
  const n = name.trim();
  if (!n) return 'Type a name for the area';
  if (n.length > 80) return 'Keep the name to 80 characters';
  return null;
}

/** A drawn area needs three corners (am:8770). */
export const canFinishArea = (points: readonly LatLng[]): boolean => points.length >= 3;

/** The area being drawn, dashed with its corners (am:8725), on the hazards layer. */
export function drawnAreaFeatures(points: readonly LatLng[]): MapFeature[] {
  const out: MapFeature[] = [];
  const st = (dashed: boolean, fill: number): MapFeature['style'] => ({
    colour: 'warn',
    stroke: 'normal',
    dashed,
    fill,
  });
  if (points.length >= 2) {
    out.push({
      id: 'draw:area',
      layer: 'hazards',
      geometry: { type: 'polygon', rings: [points] },
      style: st(true, 0.12),
      tooltip: 'Hazard area being drawn',
      identify: [{ label: 'Hazard area', value: `${points.length} corners` }],
    });
  }
  points.forEach((at, i) =>
    out.push({
      id: `draw:corner:${i}`,
      layer: 'hazards',
      geometry: { type: 'point', at },
      style: st(false, 0),
      tooltip: `Corner ${i + 1}`,
      identify: [{ label: 'Corner', value: `${i + 1}` }],
    }),
  );
  return out;
}

/** The state of a progress row, said in words (the tool showed only ✓ ⚠ ✕). */
export type HazardStepStatus = 'active' | 'done' | 'warn' | 'error';

export const HAZARD_STEP_WORD: Readonly<Record<HazardStepStatus, string>> = {
  active: 'Working',
  done: 'Done',
  warn: 'Warning',
  error: 'Failed',
};

/** A `hazard-progress` event of the core. */
export interface HazardProgressEvent {
  readonly key: string;
  readonly label: string;
  readonly status: HazardStepStatus;
  readonly detail: string;
  readonly fraction: number;
}

export interface HazardProgressRow {
  readonly key: string;
  readonly label: string;
  readonly status: HazardStepStatus;
  readonly detail: string;
}

/** The progress panel (`hazardProgress`, am:12873): one row per step, kept open at the end until closed. */
export interface HazardProgress {
  readonly title: string;
  readonly rows: readonly HazardProgressRow[];
  readonly fraction: number;
  readonly state: 'running' | 'done' | 'warning' | 'failed';
  /** The summary lines after the title. */
  readonly summary: readonly string[];
}

export function hazardProgressStart(name: string): HazardProgress {
  return { title: `Generating ${name}`, rows: [], fraction: 0, state: 'running', summary: [] };
}

export function hazardProgressApply(p: HazardProgress, e: HazardProgressEvent): HazardProgress {
  const row: HazardProgressRow = { key: e.key, label: e.label, status: e.status, detail: e.detail };
  const i = p.rows.findIndex((r) => r.key === e.key);
  const rows = i < 0 ? [...p.rows, row] : p.rows.map((r, j) => (j === i ? row : r));
  return { ...p, rows, fraction: Math.max(0, Math.min(1, e.fraction)) };
}

/** The end: the first summary line becomes the title (`done(summary)`). */
export function hazardProgressDone(
  p: HazardProgress,
  lines: readonly string[],
  warning: boolean,
): HazardProgress {
  return {
    ...p,
    title: lines[0] ?? p.title,
    summary: lines.slice(1),
    fraction: 1,
    state: warning ? 'warning' : 'done',
  };
}

export function hazardProgressFailed(p: HazardProgress, message: string): HazardProgress {
  return {
    ...p,
    title: 'Hazard generation failed',
    rows: [...p.rows, { key: '_error', label: message, status: 'error', detail: '' }],
    state: 'failed',
  };
}
